import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  keyframes,
} from '@mui/material';
import { MuiSwitchableThemeProvider } from '@farmshare/ui-components';
import { useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Agriculture } from '@mui/icons-material';

// Define the driving animation
const driveAnimation = keyframes`
  0% {
    left: 0;
    transform: rotateY(0deg);
  }
  45% {
    left: calc(100% - 48px);
    transform: rotateY(0deg);
  }
  50% {
    left: calc(100% - 48px);
    transform: rotateY(180deg);
  }
  95% {
    left: 0;
    transform: rotateY(180deg);
  }
  100% {
    left: 0;
    transform: rotateY(0deg);
  }
`;

function ErrorBoundaryFallback({
  error,
  errorId,
}: {
  error: Error;
  errorId: string | null;
}) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <MuiSwitchableThemeProvider>
      <Box sx={{ height: '100vh' }}>
        <Backdrop open sx={{ px: 1 }}>
          <Card
            sx={{
              maxWidth: 900,
              mx: 'auto',
              my: 2,
              overflowY: 'scroll',
              maxHeight: '90dvh',
            }}
          >
            <CardHeader
              sx={{ m: 2 }}
              title="Uh Oh"
              subheader="Looks like a cow got loose in our server room..."
            />
            <CardContent sx={{ px: 2, py: 0 }}>
              <Typography variant="body1" sx={{ m: 2, lineHeight: 2 }}>
                Unfortunately something has gone wrong and caused the
                application to crash. Don&apos;t worry we&apos;ve been notified
                and will be looking into it. You can refresh or go back to the
                previous page.
              </Typography>

              <Alert severity="info" icon={false} sx={{ mx: 2 }}>
                Error Id: {errorId} <br />
              </Alert>

              {showDetails ? (
                <Alert
                  severity="error"
                  icon={false}
                  sx={{
                    m: 2,
                    px: 2,
                    maxHeight: 300,
                    overflow: 'scroll',
                    display: 'flex',
                  }}
                >
                  <pre>{error?.stack}</pre>
                </Alert>
              ) : (
                <Button
                  variant="text"
                  sx={{ m: 2 }}
                  onClick={() => setShowDetails(true)}
                >
                  Click here to see the full error
                </Button>
              )}
              <Box
                sx={{
                  width: '100%',
                  position: 'relative',
                  height: 30,
                  mb: 2,
                  mx: 2,
                  border: '1px solid transparent', // For debugging - helps define boundaries
                }}
              >
                <Agriculture
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    fontSize: 30,
                    color: 'primary.main',
                    animation: `${driveAnimation} 10s ease-in-out infinite`,
                    transformOrigin: 'center center',
                    transform: 'translateY(-50%)', // Center vertically
                  }}
                />
              </Box>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button
                sx={{ m: 2 }}
                variant="outlined"
                color="primary"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
              <Button
                sx={{ m: 2 }}
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
            </CardActions>
          </Card>
        </Backdrop>
      </Box>
    </MuiSwitchableThemeProvider>
  );
}

export function RouterErrorBoundary() {
  const error = useRouteError();

  // Convert router error to a format our ErrorBoundary expects
  let errorToReport: Error;

  if (isRouteErrorResponse(error)) {
    // This is a Response-type error (like 404, 500, etc.)
    errorToReport = new Error(
      `${error.status} ${error.statusText}: ${
        error.data?.message || error.data || 'Unknown error'
      }`,
    );
  } else if (error instanceof Error) {
    // This is already an Error object
    errorToReport = error;
  } else {
    // This is some other type of error
    errorToReport = new Error(String(error));
  }

  // Report to Sentry
  Sentry.captureException(errorToReport);

  // Create a mock error boundary component that renders the same UI
  // but works with router errors
  return (
    <ErrorBoundaryFallback
      error={errorToReport}
      errorId={Sentry.lastEventId() || null}
    />
  );
}
